/**
 * Sandbox storage by lifetime — the workspace table.
 *
 * Every byte we write on a user's box has a home and a lifetime, and this is
 * the one place that says which. `data` is the user's own work (the cloud copy
 * is the truth); `cache` is rebuildable and evictable under pressure; `temp`
 * never outlives the turn; `state` is small, precious and local (login cookies,
 * what the user installed). Sweepers, the tidy pass, the mirror and the Files
 * library's watch all read this table instead of carrying their own hand list
 * of paths.
 *
 * One caveat for callers: `root` is a literal path on every row except the
 * `{derived}` one, where it is a pattern. Ask `lifetimeOf()` — never build a
 * path with `join(workspaceRoot, entry.root)` without checking for the sentinel.
 */
export const VESTRA_DIR = '.vestra';
export type Lifetime = 'data' | 'cache' | 'temp' | 'state';
export interface LifetimeEntry {
  root: string;
  lifetime: Lifetime;
  mirrored: boolean;
  capBytes?: number;
  retentionDays?: number;
  /**
   * Where this cache comes back FROM after it is evicted. A cache with `rebuiltFrom` is
   * evicted LAST, and never until the restore that reads its record exists (Phase 3) — a
   * `.vestra/tools` swept today comes back only because a human reinstalls it.
   */
  rebuiltFrom?: 'tools.json' | 'mirror';
  /**
   * COLD ARCHIVE. Each top-level entry under this root leaves the box this many days after
   * the archiver FIRST SAW it: a verified archive in Storage, a `workspace_archives` row, and
   * only then the removal (apps/sandbox services/archive). The clock is the archiver's own
   * ledger, never an mtime: fsck and a rename both keep an inode's original one. Only a row
   * that declares this is archivable, and no row the user works in ever does.
   */
  archiveAfterDays?: number;
  /**
   * Do files under this root appear in the user's Files library, and so as a task's outputs?
   * Only the user's own `files` does. App source and its build output are the Apps panel's,
   * never a pile of documents (task ddf43b7f, 2026-09-30: a repo cloned into `files/apps/bash`
   * became 977 library rows and 824 task outputs). Ask `isLibraryFilePath()`, never this
   * field alone: that is where the hidden and derived trees below a library root are refused.
   */
  library: boolean;
  note: string;
}
const MiB = 1024 * 1024;
export const CACHE_CAP_BYTES = 500 * MiB;
export const WORK_RETENTION_DAYS = 7;
export const JOURNAL_RETENTION_DAYS = 14;
/**
 * The most the task journals may hold on a box at once (`.kernel-journal`), whatever their
 * age (2026-09-28). Fourteen days of retention bound nothing on a busy box: one prod volume
 * held 476 MiB of journals. Over the cap, the oldest journals that are not live, idle over a
 * day and held WHOLE by the mirror go, until it fits (tidy/journal.ts); a journal the mirror
 * does not hold whole is never taken. A task whose journal went resumes from the mirror.
 */
export const JOURNAL_CAP_BYTES = 128 * MiB;
/**
 * How long a context spill outlives the task that wrote it.
 *
 * Shorter than the journal's fourteen days, and deliberately not the same number: the
 * journal is a transcript the cloud holds a copy of, so its local retention is about a
 * cache. A spill is the only copy of an elided tool output, and what it protects is a
 * RESUME OF THAT SAME TASK — which is why R70 refused to prune it on the journal's rule.
 * The clock that binds it is the task's own, and no task resumes a week after it ended.
 */
export const SPILL_RETENTION_DAYS = 7;
/**
 * The most the context spills may hold on a box at once, whatever their age (2026-09-28).
 *
 * The week-long clock alone does not bound them: a busy box ends tasks faster than a week
 * retires them, and one prod box held 277 MiB of spills behind a head that no longer fit.
 * Over the cap, the spills of ENDED tasks go, oldest first, until it is under; never a live
 * task's, and never one written in the last day (tidy/spills.ts). The tasks active in a week
 * hold ~0.8 MiB between them, so this is room for an unusually heavy week, not a target.
 */
export const SPILL_CAP_BYTES = 64 * MiB;
export const LOG_RETENTION = { days: 7, bytes: 100 * MiB } as const;
/**
 * The two archivable roots. A week for the layout migration's quarantine: its bytes were
 * the user's `scratch/`, and a week is long enough to notice something missing before it
 * leaves (and it comes back by restore after that). Three days for `lost+found`: its one
 * reader, the SDK session crash recovery (stream/session-support.ts), reads it on the resume
 * after a crash, which a weekend should cover; and what it holds is small, because the
 * gigabyte frozen packs are taken at every sweep (tidy/orphans.ts), never archived.
 */
export const LEGACY_ARCHIVE_AFTER_DAYS = 7;
export const LOST_FOUND_ARCHIVE_AFTER_DAYS = 3;
/**
 * Derived dirs inside an app folder: cache, never mirrored, evicted under pressure.
 *
 * Every name here is one a TOOL rebuilds on demand — a dependency tree, a build output,
 * or a downloaded runtime. The browser bundles joined the list on 2026-09-14: two prod
 * boxes hit 95% and 99% of their volume, and 1.06 GB of that was Playwright's Chromium
 * and Remotion's headless shell, downloaded onto a box whose image already ships Chrome.
 * A tree that comes back from a network fetch is a cache, not the user's work.
 */
export const DERIVED_DIR_NAMES = [
  'node_modules',
  '.venv',
  'venv',
  // a Python dependency tree outside any venv (`pip install --target`); the deleted hand lists
  // refused it too, and like node_modules it is rebuilt by the install that made it
  'site-packages',
  'dist',
  '.next',
  '.turbo',
  '__pycache__',
  '.cache',
  // browser bundles a tool downloads for itself
  'ms-playwright',
  'playwright-browsers',
  '.remotion',
  'chrome-headless-shell',
  // package and build caches of the toolchains a box installs
  '.pnpm-store',
  '.gradle',
  '.m2',
] as const;

/** Order matters: the first matching root wins, so derived dirs precede their app. */
export const WORKSPACE_LIFETIMES: readonly LifetimeEntry[] = [
  {
    root: 'files/apps/*/{derived}',
    lifetime: 'cache',
    mirrored: false,
    library: false,
    note: 'dependencies and build output of an app, at any depth; rebuilt on Start',
  },
  {
    // Below the derived row (which must win for an app's node_modules) and above `files`
    // (which would otherwise claim the app as the user's documents).
    root: 'files/apps',
    lifetime: 'data',
    mirrored: false,
    library: false,
    note: 'app source; the Apps panel shows the app, the library never lists its files',
  },
  { root: 'files', lifetime: 'data', mirrored: true, library: true, note: 'My files; the cloud copy is the truth' },
  { root: 'memory', lifetime: 'data', mirrored: true, library: false, note: 'the brain; bundled to Storage' },
  {
    root: `${VESTRA_DIR}/cookies`,
    lifetime: 'state',
    mirrored: false,
    library: false,
    note: 'Chrome login state only',
  },
  {
    root: `${VESTRA_DIR}/tools.json`,
    lifetime: 'state',
    mirrored: false,
    library: false,
    note: 'what the user installed',
  },
  {
    root: `${VESTRA_DIR}/uploads-manifest.json`,
    lifetime: 'state',
    mirrored: false,
    library: false,
    note: 'every file the uploads sync placed under files/: the only files the library mirror may ever delete',
  },
  {
    root: `${VESTRA_DIR}/archive-ledger.json`,
    lifetime: 'state',
    mirrored: false,
    library: false,
    note: 'when the archiver first saw each archivable entry; an archive is judged by it, never by mtime',
  },
  {
    root: `${VESTRA_DIR}/tools`,
    lifetime: 'cache',
    mirrored: false,
    library: false,
    rebuiltFrom: 'tools.json',
    note: 'installed tools; rebuilt from tools.json',
  },
  {
    root: `${VESTRA_DIR}/journal`,
    lifetime: 'cache',
    mirrored: true,
    library: false,
    retentionDays: JOURNAL_RETENTION_DAYS,
    rebuiltFrom: 'mirror',
    note: 'transcript cache; the mirror is the truth',
  },
  {
    root: `${VESTRA_DIR}/work`,
    lifetime: 'cache',
    mirrored: false,
    library: false,
    retentionDays: WORK_RETENTION_DAYS,
    note: 'per-task in-progress files',
  },
  {
    // Its own root, deliberately NOT under `work/` (R108): the work step deletes anything
    // under `${VESTRA_DIR}/work/<x>` seven days after that tree's newest mtime, and a
    // rename preserves mtimes — so quarantining into work/ would be deletion on a delay.
    root: `${VESTRA_DIR}/legacy`,
    lifetime: 'data',
    mirrored: false,
    library: false,
    archiveAfterDays: LEGACY_ARCHIVE_AFTER_DAYS,
    note: 'quarantined by the layout migration; never swept; moves to cold storage verified, restorable',
  },
  {
    root: `${VESTRA_DIR}/cache`,
    lifetime: 'cache',
    mirrored: false,
    library: false,
    capBytes: CACHE_CAP_BYTES,
    note: 'package caches, models, previews',
  },
  {
    root: `${VESTRA_DIR}/tmp`,
    lifetime: 'temp',
    mirrored: false,
    library: false,
    note: 'never outlives the turn',
  },
  {
    // `state`, beside the browser's cookies and for the same reason: what lives here is the
    // user's OWN credential for the engine they signed into, plus that CLI's transcripts.
    // Nothing regenerates it — losing it means asking them to sign in again. Never mirrored:
    // a mirror would carry that credential off the box and into cloud Storage.
    root: `${VESTRA_DIR}/engines`,
    lifetime: 'state',
    mirrored: false,
    library: false,
    note: "vendor engine homes (Claude Code: the user's own credential + its transcripts)",
  },
  {
    // Platform API v1 (L17): a company deployment's repos and files on its user's computer, one
    // folder per environment version. Rebuilt from the deployment's environment block, so never
    // mirrored and never in the workspace head (head.ts). Bounded by the apply's caps and by
    // keeping the two newest versions. Never present on a person's box.
    root: `${VESTRA_DIR}/company`,
    lifetime: 'cache',
    mirrored: false,
    library: false,
    note: "a company deployment's repos and files; rebuilt from the deployment",
  },
  {
    // VERSION_PINS_V1: the company skills and notebooks a deployment version froze, one folder per
    // revision (content-addressed), read by that version's tasks in place of the live shelf. Fetched
    // again from the server on any computer that lacks them, so never mirrored and never in the
    // workspace head (head.ts). Bounded by keeping the newest MAX_PINNED (version-pins.ts).
    root: `${VESTRA_DIR}/pinned`,
    lifetime: 'cache',
    mirrored: false,
    library: false,
    note: "a company version's frozen skills and notebooks; fetched again from the server",
  },
  // The agent's own homes on the volume (Rulings R57, R60). Declared, not swept: the
  // sweeper acts on the steps it is written with, never on a table row, so adding these
  // costs nothing at runtime and buys a table that tells the truth about the box. Each
  // was an exception in the writer allowlist until it earned its row here.
  {
    root: '.claude',
    lifetime: 'state',
    mirrored: false,
    library: false,
    note: 'installed skills',
  },
  {
    root: '.agents',
    lifetime: 'state',
    mirrored: false,
    library: false,
    note: 'installed agent skill roots; rebuilt by reinstall',
  },
  {
    // Agents first (train 1c). `data`, mirrored, no retention, no cap: a notebook is the one
    // memory an agent owns, so it travels in the head pack like `memory/` and nothing ever
    // sweeps it. Deliberately in NO head exclude list (head.ts: a nested exclude is deleted
    // on a move).
    root: '.team',
    lifetime: 'data',
    mirrored: true,
    library: false,
    note: "each agent's notebook (.team/<handle>/notebook.md); written only by that agent",
  },
  {
    root: '.forge-home',
    lifetime: 'cache',
    mirrored: true,
    library: false,
    retentionDays: JOURNAL_RETENTION_DAYS,
    note: 'SDK transcripts; the journal mirror is the truth',
  },
  // today's names, until the migration renames them (Phase 5); same lifetimes
  {
    root: '.kernel-journal',
    lifetime: 'cache',
    mirrored: true,
    library: false,
    retentionDays: JOURNAL_RETENTION_DAYS,
    capBytes: JOURNAL_CAP_BYTES,
    note: 'legacy journal path; over the cap, the oldest journals the mirror holds whole go',
  },
  {
    root: '.kernel-offload',
    lifetime: 'cache',
    // Ruling R70. The journal has a mirror; its SPILLS do not. Nothing uploads an
    // offloaded tool result, so a pruned spill is an elided output a resume can
    // never get back — and the row said `mirrored: true`, which is what let the
    // sweeper prune it beside the journal on the journal's rule.
    //
    // That left the retention waiting for a mirror which was never coming, so a spill
    // was immortal — 271 MB of them on the prod box that hit 99%. The mirror was never
    // the question: a spill protects a RESUME OF ITS OWN TASK, so its clock is the
    // task's, and a week after that task ended there is no resume left to protect.
    mirrored: false,
    library: false,
    retentionDays: SPILL_RETENTION_DAYS,
    capBytes: SPILL_CAP_BYTES,
    note: 'context spills; never uploaded — expire a week after the task ends, and ended tasks go oldest first over the cap',
  },
  {
    root: '.thumbs',
    lifetime: 'cache',
    mirrored: true,
    library: false,
    note: 'legacy preview path; deleted once uploaded',
  },
  {
    root: '.chrome-profile',
    lifetime: 'state',
    mirrored: false,
    library: false,
    note: 'legacy profile path; cache dirs inside are purgeable',
  },
  {
    root: '.cas',
    lifetime: 'cache',
    mirrored: true,
    library: false,
    note: 'runner releases; active plus previous only',
  },
  {
    // NO retention, deliberately. The layout migration is the only thing that touches
    // `scratch`, and it either quarantines the folder whole or LEAVES IT ALONE because a
    // registered app is still inside one that could not move (C2). A sweeper honouring a
    // 7-day rule here would delete exactly that app — the user's running code, on a timer,
    // in the one case the migration went out of its way to protect.
    root: 'scratch',
    lifetime: 'cache',
    mirrored: false,
    library: false,
    note: 'legacy working folder; the layout migration quarantines it, nothing sweeps it',
  },
  {
    // Engineer teammates (2026-09-30): the data disk for a project's local stack, an ext4 image
    // (`volumes.ext4`) that apps/sandbox bin/vestra-docker mounts over a loop device, because a
    // database cannot live on the volume itself: it is virtiofs, where every file belongs to root
    // whatever chown says, and postgres must own its data directory. `data`, because what is in
    // it is the user's working database, and nothing may sweep it or evict it; nothing rebuilds
    // it either. Never mirrored: a running database's files copied mid-write are not a database.
    // Never in the head (head.ts HEAD_DROPS_DESPITE_DATA_LIFETIME): the same torn copy, and a
    // multi-GB binary tarred at every turn. Bounded by construction: the image is reserved in full
    // (never sparse: a sparse disk on a full volume loses database writes silently) at 2 GiB or a
    // quarter of the volume's free space, and with the disk guard on it is never made past 95%.
    root: '.docker-volumes',
    lifetime: 'data',
    mirrored: false,
    library: false,
    note: "a project's local databases (Docker's data disk); kept on the box, never swept, never packed",
  },
  {
    // The package shelf (2026-10-02): the writable layer over the image's shared pnpm store and
    // uv cache, an ext4 image (`upper.ext4`) that apps/sandbox bin/vestra-shelf loop-mounts at
    // boot. `cache`, because everything in it came from a registry and comes back from one, but
    // with no cap and no retention, so no sweep takes it: it is a live, mounted filesystem, and
    // projects' node_modules and venvs are symlinks into it. It bounds itself (sparse, at most
    // 4 GiB or half the volume's free space). Never mirrored, and never in the head (head.ts):
    // a mounted filesystem copied mid-write is torn, and it is gigabytes of other people's code.
    root: '.vestra-shelf',
    lifetime: 'cache',
    mirrored: false,
    library: false,
    note: 'the package shelf\'s writable layer (what installs added); kept on the box, never swept, never packed',
  },
  {
    // `data` because nobody knows what it is: fsck reattaches a file here when its directory
    // entry was lost, so it may be the user's work. The frozen workspace pack is the one shape
    // deleted outright (tidy/orphans.ts); everything else leaves only by a verified archive.
    root: 'lost+found',
    lifetime: 'data',
    mirrored: false,
    library: false,
    archiveAfterDays: LOST_FOUND_ARCHIVE_AFTER_DAYS,
    note: "what ext4's fsck reattached; bytes of unknown provenance, archived verified, never swept",
  },
];

/** The roots whose rows declare an archive policy, derived from the table and nowhere else. */
export const ARCHIVABLE_ROOTS: ReadonlyArray<{ root: string; archiveAfterDays: number }> = WORKSPACE_LIFETIMES.flatMap(
  (e) => (e.archiveAfterDays === undefined ? [] : [{ root: e.root, archiveAfterDays: e.archiveAfterDays }]),
);

const APP_PREFIX = 'files/apps/';

/**
 * A dependency tree is derived wherever it sits inside an app, not just at the
 * top: a monorepo app keeps `web/node_modules`, a Python service keeps
 * `services/api/.venv`. So we scan every segment below the app name rather than
 * anchoring at one depth. The app's own folder name never counts — an app may
 * legitimately be called `dist`.
 */
function matchesDerived(rel: string): boolean {
  if (!rel.startsWith(APP_PREFIX)) return false;
  const belowApp = rel.slice(APP_PREFIX.length).split('/').slice(1);
  return belowApp.some((segment) => (DERIVED_DIR_NAMES as readonly string[]).includes(segment));
}

function matches(root: string, rel: string): boolean {
  if (root.includes('{derived}')) return matchesDerived(rel);
  return rel === root || rel.startsWith(root + '/');
}

/**
 * The entry that owns `relPath` (relative to the workspace root), or undefined.
 *
 * A path with a `..` segment is REFUSED (undefined), never resolved: `files/../.vestra/tools`
 * would otherwise answer 'data, mirrored' on the strength of its first segment, and a caller
 * that trusts the answer would mirror the user's tools dir to the cloud, or refuse to sweep
 * it. Callers that legitimately hold a messy path normalize it before asking.
 */
export function lifetimeOf(relPath: string): LifetimeEntry | undefined {
  const rel = relPath.replace(/^\/+/, '');
  if (rel.split('/').includes('..')) return undefined;
  return WORKSPACE_LIFETIMES.find((e) => matches(e.root, rel));
}

/**
 * Does this path (relative to the workspace root) belong in the user's Files library, and so
 * as a task's output? The ONE rule the box's watch and the server's FILES_CHANGED handler
 * both ask, built from the table and the derived catalog and nothing else.
 *
 * Its row must say `library`, and no segment below the top-level folder may be hidden (`.git`,
 * `.venv`) or a derived tree (DERIVED_DIR_NAMES, exact membership) — at ANY depth, not only
 * inside an app: one `npm install` in `files/foo` is the same leak as one in an app. That is a
 * LISTING rule. It changes nothing the sweepers evict: a folder of the user's called `dist`
 * is still their data, it just never becomes library rows.
 *
 * A directory answers for everything under it (every refusal holds for its descendants), so a
 * walk may prune a directory this refuses.
 */
export function isLibraryFilePath(workspaceRelPath: string): boolean {
  const rel = workspaceRelPath.replace(/^\/+/, '');
  const below = rel.split('/').slice(1);
  if (below.some((s) => s.startsWith('.') || (DERIVED_DIR_NAMES as readonly string[]).includes(s))) return false;
  return lifetimeOf(rel)?.library === true; // a `..` anywhere has no row, so it is refused here
}

/** Build a path under the hidden folder: vestraPath('/ws', 'tmp') === '/ws/.vestra/tmp'. */
export function vestraPath(root: string, ...segments: string[]): string {
  return [root, VESTRA_DIR, ...segments].join('/');
}
